<template>
    <card-grid-item
        :tab-widget="tabWidget"
        :card-class="['card-chart']"
        :min-w="3"
        :min-h="minHeight"
        @tab-widget-settings-click="handleTabWidgetSettingsClick"
    >
        <template v-slot:card-title>
            {{ title }}
        </template>
        <template v-slot:card-body>
            <busy-modal :show="busy" class="chart-widget-spinner" />
            <vue-apex-charts v-if="ready" height="400" :options="options" :series="series" />
        </template>
        <template v-slot:tab-widget-settings-modal>
            <tab-widget-settings-modal-form
                :modal-id="tabWidgetSettingsModalID"
                :update="updateTabWidgetSettings"
                :initial-values="tabWidgetSettingsInitialValues"
                :with-chart-period="true"
                :with-chart-interval="true"
                @success-update="handleSuccessTabWidgetSettingsUpdate"
            ></tab-widget-settings-modal-form>
        </template>
    </card-grid-item>
</template>

<script>
import API from '../../../../api';
import BusyModal from '../../../generic/busy-modal/busy-modal.vue';
import CardGridItem from '../common/card-grid-item.vue';
import { DASHBOARD_AVERAGE_TRIP_DURATION_COUNT_BY_DATE_LIST_ENDPOINT } from '../../../../endpoints';
import {
    DEFAULT_DASHBOARD_CHART_INTERVAL,
    getApexChartsGradientFill,
    getApexChartsDateFormatBasedOnInterval,
    getDefaultChartPeriodBasedOnInterval,
} from '../helpers';
import TabWidgetSettingsModalForm from '../common/tab-widget-settings-modal-form.vue';
import VueApexCharts from 'vue3-apexcharts';
import cardGridItemBaseMixin from '../common/card-grid-item-base-mixin';

export default {
    name: 'AverageTripDurationChartCardGridItem',
    mixins: [cardGridItemBaseMixin],

    components: {
        BusyModal,
        CardGridItem,
        VueApexCharts,
        TabWidgetSettingsModalForm,
    },
    computed: {
        tabWidgetSettingsInitialValues() {
            return {
                settings: {
                    name: this.tabWidget.settings.name || this.defaultName,
                    chart_interval: this.tabWidget.settings.chart_interval,
                    chart_period: this.tabWidget.settings.chart_period,
                },
            };
        },
        series() {
            return [
                {
                    data: this.tripDuration.map((trip) => trip.average_trip_duration),
                },
            ];
        },
        options() {
            return {
                chart: {
                    type: 'area',
                    zoom: {
                        enabled: false,
                    },
                    toolbar: {
                        show: false,
                    },
                },
                dataLabels: {
                    enabled: false,
                },
                stroke: {
                    curve: 'straight',
                },
                fill: getApexChartsGradientFill(),
                grid: {
                    xaxis: {
                        lines: {
                            show: true,
                        },
                    },
                    padding: {
                        right: 30,
                        bottom: 10,
                    },
                },
                xaxis: {
                    type: 'datetime',
                    categories: this.tripDuration.map((item) => item.date),
                    labels: {
                        datetimeFormatter: {
                            month: 'MMM',
                            day: 'MMM dd',
                        },
                    },
                },
                yaxis: {
                    labels: {
                        formatter: (val) => `${val}`,
                    },
                },
                tooltip: {
                    custom(data) {
                        const series = data.series && data.series[data.seriesIndex];
                        const seriesValue = series && series[data.dataPointIndex];
                        return `${'<div class="px-1 py-50"><span>'}${seriesValue || 0}</span></div>`;
                    },
                    x: {
                        format: getApexChartsDateFormatBasedOnInterval(this.tabWidget.settings.chart_interval),
                    },
                },
            };
        },
    },
    mounted() {
        this.fetchTripDurationCount();
    },
    data() {
        return {
            busy: null,
            tripDuration: [],
        };
    },
    methods: {
        async fetchTripDurationCount() {
            try {
                this.busy = true;
                const interval = this.tabWidget.settings.chart_interval || DEFAULT_DASHBOARD_CHART_INTERVAL;
                const response = await API.get(DASHBOARD_AVERAGE_TRIP_DURATION_COUNT_BY_DATE_LIST_ENDPOINT(), {
                    params: {
                        period: this.tabWidget.settings.chart_period || getDefaultChartPeriodBasedOnInterval(interval),
                        interval,
                    },
                });
                this.tripDuration = response.data;
            } catch {
                this.errorToast({ text: 'Could not load trip duration.' });
            } finally {
                this.busy = false;
            }
        },
    },
};
</script>
